import { useState, useEffect } from "react";
import { createTicket, fetchAccountDetails, fetchTickets, fetchAllAccounts, fetchContactsByAccount, createContact } from "../../api/ticketData";
import { useTickets } from "../../contexts/TicketContext";
import { useAuthUser } from "../../contexts/AuthUserContext";

import { Icon } from "@iconify/react/dist/iconify.js";

// Sample ticket object sent to the API
// {
//     "subject": "Login issue",
//     "description": "Unable to log in to the portal.",
//     "contactId": "12345",
//     "departmentId": "67890",
//     "classification": "Problem",
//     "priority": "High",
//     "channel": "Web",
//     "language": "en"
// }

const NewTicket = () => {
    const { addTicket } = useTickets();
    const user = useAuthUser();
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(false);
    const [userAccount, setUserAccount] = useState(null);
    const [loadingAccount, setLoadingAccount] = useState(true);

    // Admin accounts dropdown state
    const [allAccounts, setAllAccounts] = useState([]);
    const [loadingAllAccounts, setLoadingAllAccounts] = useState(false);
    const [accountsError, setAccountsError] = useState(null);

    // Initialize ticket state with required fields
    const [ticket, setTicket] = useState({
        subject: "",
        description: "",
        email: "", // Will be set from user's Auth0 email
        contactId: "", // Will be set when contact is found/created
        departmentId: "1094595000000006907",
        classification: "Question",
        priority: "Medium",
        channel: "Web",
        language: "en",
        accountId: "", // Will be set based on user organization
    });

    // Check if user is helios admin
    const isHeliosAdmin = user?.role === 'helios_admin' || user?.roles?.includes('helios_admin');

    // Load all accounts for admin dropdown
    const loadAllAccounts = async () => {
        if (!isHeliosAdmin) return;

        setLoadingAllAccounts(true);
        setAccountsError(null);
        
        try {
            const response = await fetchAllAccounts();
            // Transform accounts to have consistent structure
            const formattedAccounts = response.map(account => ({
                accountId: account.accountId || account.id,
                accountName: account.accountName || account.name,
            }));
            const sortedAccounts = formattedAccounts.sort((a, b) => a.accountName.localeCompare(b.accountName));
            setAllAccounts(sortedAccounts);
        } catch (error) {
            console.error('NewTicket: Failed to load all accounts:', error);
            setAccountsError("Failed to load accounts");
            setAllAccounts([]);
        } finally {
            setLoadingAllAccounts(false);
        }
    };

    // Helper function to find account for organization
    const findAccountForOrganization = (organizationName, accounts) => {
        if (!organizationName || !accounts || accounts.length === 0) {
            return null;
        }

        const normalizedOrgName = organizationName.toLowerCase().trim();
        
        // Try exact match first
        let account = accounts.find(acc => {
            const accountName = acc.accountName?.toLowerCase().trim() || '';
            return accountName === normalizedOrgName;
        });
        
        // Try partial match (contains organization name)
        if (!account) {
            account = accounts.find(acc => {
                const accountName = acc.accountName?.toLowerCase().trim() || '';
                return accountName.includes(normalizedOrgName) || normalizedOrgName.includes(accountName);
            });
        }
        
        // Try matching first word (fallback, but less reliable)
        if (!account) {
            const orgFirstWord = normalizedOrgName.split(' ')[0];
            account = accounts.find(acc => {
                const accountFirstWord = acc.accountName?.toLowerCase().trim().split(' ')[0];
                return accountFirstWord === orgFirstWord;
            });
        }
        
        return account || null;
    };

    // Find user's account based on organization - IMPROVED VERSION
    useEffect(() => {
        const findUserAccount = async () => {
            // Set user email from Auth0 for all users
            if (user?.email) {
                setTicket(prev => ({
                    ...prev,
                    email: user.email
                }));
            }

            // If user is helios admin, just load accounts dropdown and return
            if (isHeliosAdmin) {
                setLoadingAccount(false);
                loadAllAccounts();
                return;
            }

            // For regular users, use improved account discovery
            if (!user?.customerId && !user?.tenantId) {
                setLoadingAccount(false);
                return;
            }

            setLoadingAccount(true);
            try {
                // Get organization name from user
                let userOrganizationName = (user.customerId || user.tenantId || '').toLowerCase();
                
                console.log('NewTicket: Finding account for organization:', userOrganizationName);

                // CRITICAL FIX: Use fetchAllAccounts() instead of checking existing tickets
                // This ensures we get the correct account directly from Zoho
                const allAccounts = await fetchAllAccounts();
                console.log('NewTicket: Fetched all accounts:', allAccounts.map(acc => ({
                    accountId: acc.accountId || acc.id,
                    accountName: acc.accountName || acc.name
                })));

                // Find account using improved matching
                const foundAccount = findAccountForOrganization(userOrganizationName, allAccounts);
                
                if (foundAccount) {
                    const accountData = {
                        accountName: foundAccount.accountName || foundAccount.name,
                        accountId: foundAccount.accountId || foundAccount.id
                    };
                    
                    console.log('NewTicket: ✅ Found matching account:', accountData);
                    console.log('NewTicket: Account ID that will be sent:', accountData.accountId);
                    
                    setUserAccount(accountData);
                    // Update ticket state with the account ID
                    setTicket(prev => ({
                        ...prev,
                        accountId: accountData.accountId
                    }));
                } else {
                    console.warn('NewTicket: ❌ No account found for organization:', userOrganizationName);
                    console.warn('NewTicket: Available accounts:', allAccounts.map(acc => acc.accountName || acc.name));
                    
                    // Regular users cannot create tickets without an account
                    setUserAccount({ 
                        accountName: `Account Required - Contact Support`, 
                        accountId: null,
                        canCreateTickets: false 
                    });
                    console.log('NewTicket: Blocking regular user - no account found');
                }
            } catch (error) {
                console.error('NewTicket: Error finding user account:', error);
                
                // For regular users, show error and block ticket creation
                setUserAccount({ 
                    accountName: "Error loading account", 
                    accountId: null,
                    canCreateTickets: false 
                });
            } finally {
                setLoadingAccount(false);
            }
        };

        if (user) {
            findUserAccount();
        }
    }, [user]);

    // Handle input changes
    const handleChange = (e) => {
        const { name, value } = e.target;
        setTicket((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Handle admin account selection
    const handleAccountChange = (e) => {
        const selectedAccountId = e.target.value;
        const selectedAccount = allAccounts.find(acc => acc.accountId === selectedAccountId);
        
        setTicket(prev => ({
            ...prev,
            accountId: selectedAccountId
        }));

        console.log('NewTicket: Admin selected account:', selectedAccount);
    };

    // Get or create contact in the specified account
    const getOrCreateContact = async (email, accountId) => {
        try {
            // First, try to find existing contact in this account
            const contacts = await fetchContactsByAccount(accountId);
            const existingContact = contacts.find(
                (c) => c.email === email && (c.accountId === accountId || c.account?.id === accountId)
            );

            if (existingContact) {
                console.log('NewTicket: Found existing contact:', existingContact.id);
                return existingContact.id || existingContact.contactId;
            }

            // Contact doesn't exist - create it in the specified account
            console.log('NewTicket: Creating new contact in account:', accountId);
            const emailParts = email.split('@')[0].split('.');
            const firstName = emailParts[0] || 'Contact';
            const lastName = emailParts[1] || 'User';

            const newContact = await createContact({
                email: email,
                accountId: accountId,
                firstName: firstName,
                lastName: lastName
            });

            console.log('NewTicket: Created new contact:', newContact.id || newContact.contactId);
            return newContact.id || newContact.contactId;
        } catch (error) {
            console.error('NewTicket: Error getting/creating contact:', error);
            throw new Error(`Failed to get or create contact: ${error.message}`);
        }
    };

    const handleClear = () => {
        setTicket({
            subject: "",
            description: "",
            email: user?.email || "", // Preserve the user's email
            contactId: "", // Will be set when contact is found/created
            departmentId: "1094595000000006907",
            classification: "",
            priority: "",
            channel: "",
            language: "",
            accountId: isHeliosAdmin ? "" : (userAccount?.accountId || ""), // For admin, clear selection; for regular users, preserve
        });
    };

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);

        try {
            // Validate required fields
            if (!ticket.subject || !ticket.description || !ticket.email || !ticket.departmentId) {
                throw new Error("Please fill in all required fields.");
            }

            // Validate accountId based on user type
            if (isHeliosAdmin) {
                // Helios admin must select an account from dropdown
                if (!ticket.accountId) {
                    throw new Error("Please select an account to create the ticket for.");
                }
            } else {
                // Regular users need auto-selected account and permission
                if (!ticket.accountId) {
                    throw new Error("Account is required to create tickets.");
                }
                
                // Block ticket creation if user doesn't have permission
                if (userAccount?.canCreateTickets === false) {
                    throw new Error("You don't have permission to create tickets. Please contact support.");
                }
            }

            console.log("=== NewTicket: Starting Ticket Creation ===");
            console.log("🔍 Account ID:", ticket.accountId);
            console.log("🔍 User email:", ticket.email);

            // CRITICAL: Get or create contact in the specified account before creating ticket
            console.log("📞 Getting or creating contact in account:", ticket.accountId);
            const contactId = await getOrCreateContact(ticket.email, ticket.accountId);
            console.log("✅ Contact ID obtained:", contactId);

            // CRITICAL: Ensure accountId and contactId are explicitly included in the request payload
            const ticketPayload = {
                subject: ticket.subject,
                description: ticket.description,
                accountId: ticket.accountId, // Explicitly ensure accountId is included
                email: ticket.email,
                contactId: contactId, // Use the contactId from getOrCreateContact
                departmentId: ticket.departmentId,
                classification: ticket.classification,
                priority: ticket.priority,
                channel: ticket.channel,
                language: ticket.language
            };

            console.log("=== NewTicket: Submitting Ticket ===");
            console.log("🔍 Account ID in payload:", ticketPayload.accountId);
            console.log("🔍 Contact ID in payload:", ticketPayload.contactId);
            console.log("🔍 Full ticket payload (JSON):", JSON.stringify(ticketPayload, null, 2));
            console.log("====================================");

            // Send POST request
            const response = await addTicket(ticketPayload);
            
            console.log("=== NewTicket: Ticket Created ===");
            console.log("📥 Response accountId:", response.accountId);
            console.log("📤 Requested accountId:", ticketPayload.accountId);
            console.log("📋 Response accountName:", response.accountName);
            console.log("🆔 Ticket ID:", response.id || response.ticketNumber);
            
            // CRITICAL VERIFICATION: Check if accountId actually matches
            if (!response.accountId) {
                console.error('❌ ERROR: Response has no accountId!');
            } else if (response.accountId !== ticketPayload.accountId) {
                console.error('❌ ERROR: Response accountId DIFFERS from request!', {
                    requested: ticketPayload.accountId,
                    received: response.accountId,
                    message: 'Backend may have overridden the accountId'
                });
            } else {
                console.log('✅ Frontend: Account ID in response matches request');
                console.log('⚠️  NOTE: If ticket appears in wrong account in Zoho, this is a BACKEND issue.');
                console.log('⚠️  Backend may be using contact lookup which overrides accountId.');
            }
            
            console.log("📄 Full response:", JSON.stringify(response, null, 2));
            console.log("=================================");
            setSuccess(true);
            setTicket({
                subject: "",
                description: "",
                email: user?.email || "",
                contactId: "", // Will be set when contact is found/created
                departmentId: '1094595000000006907',
                classification: "Question",
                priority: "Medium",
                channel: "Web",
                language: "en",
                accountId: isHeliosAdmin ? "" : (userAccount?.accountId || ""),
            });
        } catch (err) {
            // Display detailed validation errors
            const backendError = err.response?.data;
            if (backendError?.errors) {
                setError(`Validation errors: ${JSON.stringify(backendError.errors)}`);
            } else {
                setError(backendError?.message || err.message);
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="w-full h-full flex flex-col gap-4 lg:gap-6">

            {error && (
                <div className="responsive-card-compact bg-red-900 text-red-300 border border-red-700">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:error-outline" className="text-xl" />
                        <span className="responsive-text-small">{error}</span>
                    </div>
                </div>
            )}

            {success && (
                <div className="responsive-card-compact bg-green-900 text-green-300 border border-green-700">
                    <div className="flex items-center gap-2">
                        <Icon icon="material-symbols:check-circle-outline" className="text-xl" />
                        <span className="responsive-text-small">Ticket created successfully!</span>
                    </div>
                </div>
            )}

            <form onSubmit={handleSubmit} className="new-ticket-form-responsive">
                <div className="new-ticket-fields-responsive">
                    {/* Subject (Required) */}
                    <div className="input-container">
                        <label className="responsive-text-body">Subject <span className="text-error">*</span></label>
                        <input
                            type="text"
                            name="subject"
                            value={ticket.subject}
                            onChange={handleChange}
                            className="w-full rounded input-textbox text-font-primary placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary"
                            placeholder="Enter your subject here..."
                            required
                        />
                    </div>

                    {/* Description (Required) */}
                    <div className="input-container">
                        <label className="responsive-text-body">Description <span className="text-error">*</span></label>
                        <textarea
                            name="description"
                            value={ticket.description}
                            onChange={handleChange}
                            className="w-full rounded input-textbox text-font-primary placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary resize-none"
                            rows="4"
                            placeholder="Enter your description here..."
                            required
                        />
                    </div>

                    <div className="new-ticket-row-responsive">
                        {/* Classification (Required) */}
                        <div className="input-container">
                            <label className="responsive-text-body">Classification <span className="text-error">*</span></label>
                            <select
                                name="classification"
                                value={ticket.classification}
                                onChange={handleChange}
                                className="w-full rounded input-textbox text-font-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                required
                            >
                                <option value="Question">Question</option>
                                <option value="Problem">Problem</option>
                                <option value="Feature">Feature</option>
                            </select>
                        </div>
                        {/* Priority (Required) */}
                        <div className="input-container">
                            <label className="responsive-text-body">Priority <span className="text-error">*</span></label>
                            <select
                                name="priority"
                                value={ticket.priority}
                                onChange={handleChange}
                                className="w-full rounded input-textbox text-font-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                required
                            >
                                <option value="Low">Low</option>
                                <option value="Medium">Medium</option>
                                <option value="High">High</option>
                            </select>
                        </div>
                    </div>

                    <div className="new-ticket-row-responsive">
                        {/* Email (Auto-filled from Auth0) */}
                        <div className="input-container">
                            <label className="responsive-text-body">Email <span className="text-error">*</span></label>
                            <input
                                type="email"
                                name="email"
                                value={ticket.email}
                                onChange={handleChange}
                                className="w-full rounded input-textbox text-font-secondary placeholder-gray-500 cursor-not-allowed opacity-75"
                                placeholder="Your email address..."
                                disabled
                                readOnly
                            />
                        </div>

                        {/* Account Field - Dropdown for Admin, Auto-selected for Regular Users */}
                        <div className="input-container">
                            <label className="responsive-text-body">Account <span className="text-error">*</span></label>
                            {isHeliosAdmin ? (
                                /* Admin Dropdown */
                                <select
                                    name="accountId"
                                    value={ticket.accountId}
                                    onChange={handleAccountChange}
                                    className="w-full rounded input-textbox text-font-primary focus:outline-none focus:ring-2 focus:ring-primary"
                                    disabled={loadingAllAccounts}
                                    required
                                >
                                    <option value="">{loadingAllAccounts ? "Loading accounts..." : "Select an account"}</option>
                                    {allAccounts.map(account => (
                                        <option key={account.accountId} value={account.accountId}>
                                            {account.accountName}
                                        </option>
                                    ))}
                                </select>
                            ) : (
                                /* Regular User Auto-selected Field */
                                <input
                                    type="text"
                                    name="accountDisplay"
                                    value={loadingAccount ? "Loading account..." : userAccount?.accountName || "No account found"}
                                    className="w-full p-3 sm:p-4 rounded bg-bgSecondary shadow-neo-card-inner text-font-secondary placeholder-gray-500 cursor-not-allowed opacity-75"
                                    placeholder="Account will be auto-selected..."
                                    disabled
                                    readOnly
                                />
                            )}
                            
                            {/* Error Messages for Regular Users */}
                            {!isHeliosAdmin && !loadingAccount && !userAccount?.accountId && userAccount?.canCreateTickets === false && (
                                <p className="text-xs text-error">
                                    <Icon icon="material-symbols:error-outline" className="inline mr-1" />
                                    No account found for your organization. You cannot create tickets. Please contact support.
                                </p>
                            )}
                            
                            {/* Error Message for Admin */}
                            {isHeliosAdmin && accountsError && (
                                <p className="text-xs text-error">
                                    <Icon icon="material-symbols:error-outline" className="inline mr-1" />
                                    {accountsError}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {/* Buttons */}
                <div className="new-ticket-buttons-responsive">
                    <button
                        type="button"
                        onClick={handleClear}
                        className="new-ticket-button button-container"
                    >
                        <Icon icon="pajamas:clear-all" className="responsive-icon-sm text-error" />
                        <span>Clear</span>
                    </button>
                    <button
                        type="submit"
                        disabled={isLoading || (!isHeliosAdmin && userAccount?.canCreateTickets === false)}
                        className="new-ticket-button button-container disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <Icon icon="material-symbols-light:new-label" className="responsive-icon-sm text-secondary" />
                        <span>
                            {isLoading ? "Creating..." : 
                             (!isHeliosAdmin && userAccount?.canCreateTickets === false) ? "Contact Support" : 
                             "Create Ticket"}
                        </span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default NewTicket;